'use client'

import * as React from 'react'
import { Popover as PopoverPrimitive } from 'radix-ui'
import { cn } from '@axilon-apps/libs-ui-shadcn'

type Placement =
  | 'top'
  | 'top-start'
  | 'top-end'
  | 'bottom'
  | 'bottom-start'
  | 'bottom-end'
  | 'left'
  | 'left-start'
  | 'left-end'
  | 'right'
  | 'right-start'
  | 'right-end'

interface Props {
  anchorEl?: HTMLElement | null
  open: boolean
  onClose?: () => void
  placement?: Placement
  disablePortal?: boolean
  className?: string
  children: React.ReactNode
}

const splitPlacement = (
  placement: Placement
): { side: 'top' | 'bottom' | 'left' | 'right'; align: 'start' | 'center' | 'end' } => {
  const [side, sub] = placement.split('-') as [
    'top' | 'bottom' | 'left' | 'right',
    'start' | 'end' | undefined
  ]
  const align = sub === 'start' ? 'start' : sub === 'end' ? 'end' : 'center'
  return { side, align }
}

const Popup = ({
  anchorEl,
  open,
  onClose,
  placement = 'bottom',
  disablePortal = false,
  className,
  children
}: Props) => {
  const virtualRef = React.useMemo(() => ({ current: anchorEl ?? null }), [anchorEl])
  const { side, align } = splitPlacement(placement)
  const Wrapper = disablePortal ? React.Fragment : PopoverPrimitive.Portal

  if (!anchorEl) return null

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose?.()
      }}
    >
      <PopoverPrimitive.Anchor virtualRef={virtualRef as React.RefObject<HTMLElement>} />
      <Wrapper>
        <PopoverPrimitive.Content
          side={side}
          align={align}
          sideOffset={4}
          className={cn('z-50 outline-none', className)}
        >
          {children}
        </PopoverPrimitive.Content>
      </Wrapper>
    </PopoverPrimitive.Root>
  )
}

export const PopupContent = React.forwardRef<HTMLDivElement, React.ComponentProps<'div'>>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('bg-popover text-popover-foreground rounded-lg border shadow-md', className)}
      {...props}
    >
      {children}
    </div>
  )
)
PopupContent.displayName = 'PopupContent'

export default Popup
